import { ComponentRef, inject, Injectable, Injector } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Observable, Subject } from 'rxjs';
import { ToastContainerComponent } from './toast-container.component';

export type ToastType = 'error' | 'info' | 'success' | 'warning' | 'debug';

export interface ToastData {
  type: ToastType;
  text: string;
  duration?: number;
}

export class ToastRef {
  private readonly _afterClosed = new Subject<void>();
  private removeFn: (() => void) | null = null;

  /** @internal — called by the service to wire visual removal */
  _setRemoveFn(fn: () => void): void {
    this.removeFn = fn;
  }

  /** @internal — called when the toast is visually removed */
  _complete(): void {
    this._afterClosed.next();
    this._afterClosed.complete();
  }

  close(): void {
    if (this.removeFn) {
      const fn = this.removeFn;
      this.removeFn = null;
      fn();
    }
    this._complete();
  }

  afterClosed(): Observable<void> {
    return this._afterClosed.asObservable();
  }
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);

  private containerRef: ComponentRef<ToastContainerComponent> | null = null;
  private overlayRef: OverlayRef | null = null;
  private toastIdCounter = 0;

  show(data: ToastData): ToastRef {
    this.ensureContainer();

    const id = ++this.toastIdCounter;
    const toastRef = new ToastRef();

    // When external code calls toastRef.close(), remove from container
    toastRef._setRemoveFn(() => {
      this.containerRef?.instance.removeToast(id);
    });

    // When container removes the toast (close button, timer), complete the ref
    this.containerRef!.instance.addToast({
      id,
      data,
      closeFn: () => {
        toastRef._complete();
      }
    });

    return toastRef;
  }

  success(text: string, duration?: number): ToastRef {
    return this.show({ type: 'success', text, duration });
  }

  error(text: string, duration?: number): ToastRef {
    return this.show({ type: 'error', text, duration });
  }

  warning(text: string, duration?: number): ToastRef {
    return this.show({ type: 'warning', text, duration });
  }

  info(text: string, duration?: number): ToastRef {
    return this.show({ type: 'info', text, duration });
  }

  debug(text: string, duration?: number): ToastRef {
    return this.show({ type: 'debug', text, duration });
  }

  private ensureContainer(): void {
    if (this.containerRef) return;

    const positionStrategy = this.overlay.position()
      .global()
      .centerHorizontally()
      .top('16px');

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      panelClass: 'toast-container-panel'
    });

    const portal = new ComponentPortal(ToastContainerComponent, null, this.injector);
    this.containerRef = this.overlayRef.attach(portal);
  }
}
